import React from 'react';
import { Link } from "gatsby";
import SEO from "@/components/SEO";
import ServiceSchema from "@/components/ServiceSchema";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { BOOKING_URL, LINK_ATTRIBUTES } from "@/lib/config";
import {
  Heart,
  Shield,
  Award,
  Clock,
  CheckCircle,
  Users,
  Star,
  Stethoscope,
  AlertTriangle,
  Zap,
  ArrowRight,
  ChevronDown
} from "lucide-react";


const RootCanalTherapy = () => {
  useScrollAnimation();

  const benefits = [
    "Save your natural tooth",
    "Eliminate pain and infection",
    "Avoid tooth extraction",
    "Restore normal function"
  ];

  const signs = [
    {
      title: "Severe Tooth Pain",
      description: "Intense pain when biting down or applying pressure",
      imageSrc: "/toothache.png"
    },
    {
      title: "Temperature Sensitivity",
      description: "Prolonged sensitivity to hot or cold that lingers after the source is removed",
      imageSrc: "/sensitivity.png"
    },
    {
      title: "Swelling & Tenderness",
      description: "Swelling or tenderness in the gums near the affected tooth",
      imageSrc: "/toothache.png"
    },
    {
      title: "Tooth Discolouration",
      description: "Darkening of the tooth, which may indicate internal damage or infection",
      imageSrc: "/sensitivity.png"
    }
  ];

  const process = [
    {
      step: "1",
      title: "Diagnosis & X-Rays",
      description: "Comprehensive examination and imaging to confirm the need for root canal treatment"
    },
    {
      step: "2",
      title: "Local Anaesthesia",
      description: "Comfortable numbing of the area to ensure a pain-free procedure"
    },
    {
      step: "3",
      title: "Access Opening",
      description: "Small opening made in the crown of the tooth to access the pulp chamber"
    },
    {
      step: "4",
      title: "Cleaning & Shaping",
      description: "Remove infected pulp and bacteria, then clean and shape the root canals"
    },
    {
      step: "5",
      title: "Filling & Sealing",
      description: "Fill the cleaned canals with biocompatible material and seal the opening"
    },
    {
      step: "6",
      title: "Crown Placement",
      description: "Place a crown to protect and restore the tooth's full function"
    }
  ];

  const myths = [
    {
      myth: "Root canals are extremely painful",
      reality: "Modern techniques make root canals comfortable, often less painful than the toothache itself"
    },
    {
      myth: "It's better to extract the tooth",
      reality: "Saving your natural tooth is almost always the best option for long-term oral health"
    },
    {
      myth: "Root canal treatment causes illness",
      reality: "Root canals are safe and effective, with no scientific evidence linking them to illness"
    },
    {
      myth: "Root canals require many appointments",
      reality: "Most root canals can be completed in 1-2 appointments with modern techniques"
    }
  ];

  const afterCare = [
    "Take prescribed antibiotics if given",
    "Use over-the-counter pain medication as needed",
    "Avoid chewing on treated tooth until crown is placed",
    "Maintain good oral hygiene",
    "Attend follow-up appointments",
    "Get permanent crown placed promptly"
  ];

  const faqs = [
    {
      question: "Is root canal treatment painful?",
      answer: "Root canal treatment is performed under local anaesthetic. Most patients report that the procedure itself is no more uncomfortable than having a filling. Some soreness after the treatment is normal and usually settles within a few days. Your dentist can recommend appropriate pain relief."
    },
    {
      question: "Can I drive after root canal treatment?",
      answer: "In most cases, yes. Root canal treatment is generally performed under local anaesthetic, which means you are fully conscious and can drive afterward once the numbness has worn off. If sedation is used, you will need someone to drive you home. Your dentist will advise you beforehand."
    },
    {
      question: "What happens if I don't have root canal treatment?",
      answer: "Without treatment, an infected tooth can worsen and may eventually need to be extracted. Infection can also spread to surrounding tissue. Your dentist will explain the risks of leaving a tooth untreated and help you weigh up your options."
    },
    {
      question: "Do I need a crown after root canal treatment?",
      answer: "Most teeth benefit from a crown after root canal treatment to protect against fracture and restore full function. Your dentist will advise whether a crown is appropriate for your tooth based on the amount of remaining tooth structure."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO title="Root Canal Treatment Broadmeadows | Y3 Smiles Dental" description="Save your natural tooth with gentle root canal therapy at Y3 Smiles Dental in Broadmeadows. Relieve pain, eliminate infection, and protect your smile. Book an appointment." image="/og-reception.jpg" />

      <ServiceSchema
        serviceName="Root Canal Therapy"
        description={"Save your natural tooth with gentle root canal therapy at Y3 Smiles Dental in Broadmeadows. Relieve pain, eliminate infection, and protect your smile. Book an appointment."}
        url="https://y3smilesdental.com.au/treatments/root-canal/"
        priceFromAUD={1200}
      />
      <Header />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-gradient-hero py-24">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl md:text-6xl font-bold text-neutral-800 mb-6">
                Root Canal Therapy
              </h1>
              <p className="text-xl text-neutral-800 mb-8 leading-relaxed">
                Advanced endodontic treatment to save infected or severely damaged teeth.
                Modern, comfortable procedures that eliminate pain and preserve your natural smile.
              </p>
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center bg-white/80 backdrop-blur-sm rounded-full px-4 py-2">
                    <CheckCircle className="w-5 h-5 text-primary mr-2" />
                    <span className="text-sm font-medium text-neutral-700">{benefit}</span>
                  </div>
                ))}
              </div>
              <Button variant="booking" size="xl" asChild>
                <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>BOOK ROOT CANAL CONSULTATION</a>
              </Button>
            </div>
          </div>
        </section>

        {/* AHPRA Educational Introduction */}
        <section className="py-8 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <p className="text-blue-800 text-sm leading-relaxed">
                  The information on this page is intended as general education about root canal treatment. Every patient&apos;s dental health is different, and your dentist will assess your individual needs before recommending any treatment. We encourage you to ask questions and, if you have any concerns, to seek a second opinion.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Signs You Need Root Canal */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-6">
                Signs You May Need Root Canal Treatment
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {signs.map((sign, index) => {
                return (
                  <Card key={index} className="text-center border-0 shadow-soft hover:shadow-medium hover:scale-[1.02] hover:-translate-y-1 transition-smooth fade-in-section" style={{ transitionDelay: `${index * 150}ms` }}>
                    <CardHeader>
                      <div className="w-16 h-16 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-4">
                        <img src={sign.imageSrc} alt={sign.title} className="w-8 h-8" />
                      </div>
                      <CardTitle className="text-xl font-bold text-neutral-800 mb-2">
                        {sign.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-neutral-800 text-sm leading-relaxed">
                        {sign.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            <div className="mt-10 max-w-3xl mx-auto">
              <p className="text-neutral-600 text-sm text-center italic">
                These symptoms do not always indicate the need for root canal treatment. A thorough examination by your dentist is the only way to determine the appropriate course of action.
              </p>
            </div>
          </div>
        </section>

        {/* Myths vs Reality */}
        <section className="py-24 bg-neutral-50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-6">
                  Root Canal Myths vs Reality
                </h2>
                <p className="text-lg text-neutral-800">
                  Let's dispel common misconceptions about root canal treatment.
                </p>
              </div>

              <div className="space-y-6">
                {myths.map((item, index) => (
                  <Card key={index} className="border-0 shadow-soft hover:shadow-medium hover:scale-[1.02] hover:-translate-y-1 transition-smooth fade-in-section" style={{ transitionDelay: `${index * 150}ms` }}>
                    <CardContent className="pt-8 pb-6 px-6">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-red-50 rounded-lg p-4">
                          <h4 className="font-semibold text-red-700 mb-2 flex items-center">
                            <AlertTriangle className="w-5 h-5 mr-2" />
                            Myth
                          </h4>
                          <p className="text-red-600 text-sm">{item.myth}</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4">
                          <h4 className="font-semibold text-green-700 mb-2 flex items-center">
                            <CheckCircle className="w-5 h-5 mr-2" />
                            Reality
                          </h4>
                          <p className="text-green-600 text-sm">{item.reality}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-6">
                  Frequently Asked Questions
                </h2>
                <p className="text-lg text-neutral-800 mb-8">
                  Get answers to common questions about root canal therapy.
                </p>
              </div>

              <Accordion 
                type="single"
                className="w-full space-y-4"
              >
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`faq-${index}`} className="bg-white rounded-lg shadow-soft border-0">
                    <AccordionTrigger className="px-6 py-4 text-left hover:no-underline">
                      <span className="text-lg font-semibold text-neutral-800">{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-4">
                      <p className="text-neutral-800 leading-relaxed">{faq.answer}</p>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* Internal Linking Block */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-xl font-bold text-neutral-800 mb-4">Explore our services:</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: "Children's Dentistry", to: "/treatments/childrens-dentistry" },
                  { label: "Emergency Dentist", to: "/treatments/emergency" },
                  { label: "Dental Fillings", to: "/treatments/dental-fillings" },
                  { label: "Crowns and Bridges", to: "/treatments/crown-bridge" },
                  { label: "Dental Implants", to: "/treatments/dental-implants" },
                  { label: "Wisdom Teeth Removal", to: "/treatments/wisdom-teeth" },
                  { label: "Teeth Whitening", to: "/treatments/teeth-whitening" },
                  { label: "All Treatments", to: "/treatments" },
                ].map((link) => (
                  <Link key={link.to} to={link.to} className="text-sm text-primary hover:underline font-medium py-1">
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-neutral-50">
          <div className="container mx-auto px-6 text-center">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-4xl font-bold text-neutral-800 mb-6">
                Don't Let Tooth Pain Control Your Life
              </h2>
              <p className="text-lg text-neutral-800 mb-8">
                Root canal therapy can eliminate your pain and save your natural tooth.
                Modern techniques make the procedure comfortable and highly successful.
              </p>
              <Button variant="booking" size="xl" asChild>
                <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>
                  BOOK ONLINE
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* <Contact /> */}
      </main>
      <Footer />
    </div>
  );
};

export default RootCanalTherapy;
